"use client"

import React, { useMemo, useState } from "react"
import { Switch } from "@/components/ui/switch"
import { Blade, BladeSection, DetailField, PillButton } from "@/components/blades/blade"
import { MappingFlowCanvas } from "@/components/mapping-flow/mapping-flow-canvas"
import { buildMappingFlow, type FlowLink } from "@/lib/mapping-flow"
import { useElementSize } from "@/hooks/use-element-size"
import type { FlatMapping } from "@/types/ui"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

/** Past this many unmapped destinations the flow opens with them folded into per-section counts */
const UNMAPPED_FOLD_THRESHOLD = 40

export interface MappingFlowBladeProps {
  open: boolean
  onClose: () => void
  mappingName: string
  primaryEntity: string
  mappings: FlatMapping[]
  /** Opens the mapping editor for a ribbon */
  onEdit?: (mapping: FlatMapping) => void
}

/**
 * "Show me what feeds into Placement": every mapped field as a ribbon from its ATS
 * source to its FBO destination, value sets as junctions, gaps and invalid rows visible
 * at a glance. A communication and QA view, not an editor — though a ribbon can hand
 * off to the editor.
 */
export function MappingFlowBlade({ open, onClose, mappingName, primaryEntity, mappings, onEdit }: MappingFlowBladeProps) {
  const { t, n } = useTranslation()
  const unmappedCount = useMemo(() => mappings.filter((m) => !m.isMapped).length, [mappings])
  const [showUnmapped, setShowUnmapped] = useState(unmappedCount <= UNMAPPED_FOLD_THRESHOLD)
  const { ref: canvasRef, size: { width } } = useElementSize<HTMLDivElement>()
  const flow = useMemo(() => buildMappingFlow(mappings, primaryEntity, { includeUnmapped: showUnmapped, width: width || undefined }), [mappings, primaryEntity, showUnmapped, width])
  const [hovered, setHovered] = useState<FlowLink | null>(null)
  const [selected, setSelected] = useState<FlowLink | null>(null)

  const shown = hovered ?? selected
  /** A mapping with no shared set either carries its own values or none at all */
  const inlineValuesLabel = (count: number) =>
    count === 0
      ? t("mappingFlow.details.none")
      : t((count === 1 ? "mappingFlow.details.inlineValuesOne" : "mappingFlow.details.inlineValuesOther"), { count: n(count) })
  const unmappedTargets = flow.targets.filter((target) => !target.mapped && !target.collapsedCount)

  return (
    <Blade
      open={open}
      onClose={onClose}
      size="xl"
      eyebrow={mappingName}
      title={t("mappingFlow.blade.title")}
      description={t("mappingFlow.blade.description", {
        mapped: n(flow.stats.mapped),
        unmapped: n(flow.stats.unmapped),
        invalid: n(flow.stats.invalid),
        valueSets: t((flow.stats.valueSets === 1 ? "mappingFlow.blade.valueSetsOne" : "mappingFlow.blade.valueSetsOther"), { count: n(flow.stats.valueSets) }),
      })}
      bare
      footer={
        <>
          <span className="text-xs text-muted-foreground">{t("mappingFlow.blade.hint")}</span>
          <PillButton onClick={onClose}>{t("common.actions.done")}</PillButton>
        </>
      }
    >
      <div className="flex h-full min-h-[520px]">
        <div ref={canvasRef} className="relative min-w-0 flex-1" style={{ background: "radial-gradient(ellipse at 50% 40%, #16295c 0%, #0f214b 70%)" }}>
          {width > 0 && <MappingFlowCanvas flow={flow} highlighted={selected?.id ?? null} onHover={setHovered} onSelect={setSelected} />}
        </div>

        <aside className="flex w-[300px] shrink-0 flex-col border-l border-card-border">
          <label className="flex items-center gap-2 border-b border-card-border px-4 py-3 text-xs text-muted-foreground">
            <Switch checked={showUnmapped} onCheckedChange={setShowUnmapped} className="scale-90" /> {t("mappingFlow.blade.showUnmapped")}
          </label>
          <div className="min-h-0 flex-1 overflow-y-auto px-4">
            <BladeSection label={t("mappingFlow.sections.mapping")}>
              {shown ? (
                <div className="grid grid-cols-1 gap-3">
                  <DetailField label={t("mappingFlow.details.source")} mono>{shown.mapping.sourceField}</DetailField>
                  <DetailField label={t("mappingFlow.details.destination")} mono>{shown.mapping.destinationField}</DetailField>
                  <DetailField label={t("mappingFlow.details.entity")}>{shown.mapping.entityName ?? primaryEntity}</DetailField>
                  <DetailField label={t("mappingFlow.details.converter")}>{shown.converter ?? t("mappingFlow.details.none")}</DetailField>
                  <DetailField label={t("mappingFlow.details.valueSet")}>{shown.valueSet ?? inlineValuesLabel(shown.mapping.valueMappings?.length ?? 0)}</DetailField>
                  {shown.mapping.defaultValueMapping !== undefined && shown.mapping.defaultValueMapping !== null && (
                    <DetailField label={t("mappingFlow.details.default")}>{String(shown.mapping.defaultValueMapping)}</DetailField>
                  )}
                  {shown.invalid && <p className="text-xs font-semibold text-destructive">{t("mappingFlow.details.invalid")}</p>}
                  {onEdit && selected && (
                    <PillButton variant="outline" className="justify-center" onClick={() => onEdit(selected.mapping)}>
                      {t("mappingFlow.details.edit")}
                    </PillButton>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{t("mappingFlow.details.empty")}</p>
              )}
            </BladeSection>
            <BladeSection label={t("mappingFlow.sections.legend")}>
              <ul className="space-y-1 text-xs">
                <li><span className="mr-2 inline-block h-2 w-4 rounded bg-[#9db2e0]" /> {t("mappingFlow.legend.entity")}</li>
                <li><span className="mr-2 inline-block h-2 w-4 rounded bg-[#f0cd8a]" /> {t("mappingFlow.legend.valueSet")}</li>
                <li><span className="mr-2 inline-block h-2 w-4 rounded bg-[#e57373]" /> {t("mappingFlow.legend.invalid")}</li>
                <li><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#3f4d6f]" /> {t("mappingFlow.legend.unmapped")}</li>
              </ul>
            </BladeSection>
            <BladeSection label={t("mappingFlow.sections.unmapped", { count: n(flow.stats.unmapped) })}>
              {flow.stats.unmapped === 0 ? (
                <p className="text-xs text-muted-foreground">{t("mappingFlow.unmapped.none")}</p>
              ) : showUnmapped ? (
                <ul className="max-h-48 space-y-0.5 overflow-y-auto font-mono text-[11px] text-muted-foreground">
                  {unmappedTargets.map((target) => <li key={target.id}>{target.field}</li>)}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">{t("mappingFlow.unmapped.folded")}</p>
              )}
            </BladeSection>
          </div>
        </aside>
      </div>
    </Blade>
  )
}
